import React, { useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import useJsonForm from '../../../../hooks/useJsonForm';

const GeneralPracticesSummary = ({ surveyData }) => {
  const { jsonForm, loadingState } = useJsonForm('634d62ad4083e2c844a899c5');

  if (loadingState !== 'loaded' || !jsonForm) return <p>Loading summary...</p>;

  const generalPractices = surveyData?.generalPractices ?? {};

  // Check if generalPractices is empty
  if (Object.keys(generalPractices).length === 0) {
    return <h5 className="fw-bold">No general practices selected</h5>;
  }

  let parsedSchema = {};
  try {
    parsedSchema = JSON.parse(jsonForm.schema ?? '{}');
  } catch {
    parsedSchema = {};
  }

  const enums = parsedSchema?.enums ?? {};
  const enumTitleMap = {};

  Object.values(enums ?? {}).forEach(list => {
    if (list?.oneOf) {
      list.oneOf.forEach(({ const: value, title }) => {
        if (value) enumTitleMap[value] = title;
      });
    }
  });

  let parsedUiSchema = {};
  try {
    parsedUiSchema = JSON.parse(jsonForm.uiSchema ?? '{}');
  } catch {
    parsedUiSchema = {};
  }

  const questionLabelMap = {};

  const extractLabels = (elements = [], parentLabel = null) => {
    if (!Array.isArray(elements)) return;
    let lastLabel = parentLabel;

    elements.forEach(el => {
      if (!el) return;

      // Handle Label elements
      if (el.type === 'Label') {
        lastLabel = el.text;
      }

      // Handle Group and Category elements - use their label as context
      if ((el.type === 'Group' || el.type === 'Category') && el.label) {
        if (Array.isArray(el.elements)) {
          extractLabels(el.elements, el.label);
        }
        return;
      }

      // Handle Control elements
      if (el.type === 'Control' && el.scope) {
        const key = el.scope
          .replace('#/properties/', '')
          .replace(/\/properties/g, '')
          .replace(/\//g, '.');

        // Use explicit label, or lastLabel, or parentLabel as fallback
        questionLabelMap[key] = el.label || lastLabel || parentLabel || key;

        // Reset lastLabel after using it for a Control
        if (el.label === false || el.label) {
          lastLabel = parentLabel;
        }
      }

      // Recursively process nested elements
      if (Array.isArray(el.elements)) {
        extractLabels(el.elements, lastLabel || parentLabel);
      }
    });
  };

  extractLabels(parsedUiSchema?.elements);

  const questionOverrides = {
    'cropWalkingPerson.status': 'Who does the crop walking?',
  };

  const formatLabel = (label = '') => {
    if (!label || typeof label !== 'string') return '';
    const formatted = label
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
    return formatted.replace(/\bGps\b/g, 'GPS');
  };

  const getDisplayStatus = status =>
    enumTitleMap?.[status] ?? formatLabel(status ?? 'N/A');

  const renderItem = (key, item = {}) => {
    // Special handling for otherLivestock which has 'name' instead of 'status'
    if (key === 'otherLivestock' && item?.name) {
      return (
        <div key={key} className="border rounded p-3 mb-3 bg-white shadow-sm h-100">
          <h6 className="fw-bold mb-2">
            {questionLabelMap?.['otherLivestock.name'] ?? 'Other Livestock'}
          </h6>
          <p className="mb-1">
            {item.name}
          </p>
        </div>
      );
    }

    const questionKey = `${key}.status`;
    const question =
      questionOverrides?.[questionKey] ??
      questionLabelMap?.[questionKey] ??
      formatLabel(key);

    // Handle array status (like livestockTypes)
    if (Array.isArray(item?.status)) {
      const displayItems = item.status.map(val =>
        enumTitleMap?.[val] ?? formatLabel(val)
      ).join(', ');

      return (
        <div key={key} className="border rounded p-3 mb-3 bg-white shadow-sm h-100">
          <h6 className="fw-bold mb-2">{question}</h6>
          <p className="mb-1">
            {displayItems || 'None'}
          </p>
          {item?.notes && (
            <p className="mb-0">
              <strong>Notes:</strong>{' '}
              <span className="fst-italic text-secondary">{item.notes}</span>
            </p>
          )}
        </div>
      );
    }

    if (!item?.status) {
      return null;
    }

    return (
      <div key={key} className="border rounded p-3 mb-3 bg-white shadow-sm h-100">
        <h6 className="fw-bold mb-2">{question}</h6>
        <p className="mb-1">
          <strong>Status:</strong> {getDisplayStatus(item?.status)}
        </p>
        {item?.notes && (
          <p className="mb-0">
            <strong>Notes:</strong>{' '}
            <span className="fst-italic text-secondary">{item.notes}</span>
          </p>
        )}
      </div>
    );
  };

  const renderLivestockOptions = (key, item) => {

    const livestockTypesDictionary = {
      "beefCattle": "Beef Cattle",
      "dairyCattle": "Dairy Cattle",
      "pigs": "Pigs",
      "sheep": "Sheep",
      "poultry": "Poultry",
      "other": "Other"
    }

    const housingTypesDictionary = {
      "seasonlyHoused": "Seasonly Housed",
      "allYearHoused": "All Year Housed",
      "neverHoused": "Never Housed"
    }

    const livestockOwnershipDictionary = {
      "own": "Own",
      "thirdParty": "Third Party",
      "mix": "Mix of own and third party"
    }

    return (
      <>
        {Object.hasOwn(item, "livestockTypes") && item.livestockTypes !== null &&
          <div className="border rounded p-3 mb-3 bg-white shadow-sm h-100">
            <h6 className="fw-bold mb-2">Livestock</h6>
            <p className="mb-1">
              <strong>Types:</strong>{" "}
              {item.livestockTypes
                .map(livestockType => livestockTypesDictionary[livestockType])
                .join(", ")}
            </p>
            {item?.livestock?.notes && (
              <p className="mb-0">
                <strong>Notes:</strong>{' '}
                <span className="fst-italic text-secondary">{item.livestock.notes}</span>
              </p>
            )}
          </div>
        }
        {Object.hasOwn(item, "livestockOwnership") && item.livestockOwnership !== null &&
          <div className="border rounded p-3 mb-3 bg-white shadow-sm h-100">
            <h6 className="fw-bold mb-2">Livestock Ownership</h6>
            <p className="mb-1">
              <strong>Status:</strong> {livestockOwnershipDictionary[item.livestockOwnership.status]}
            </p>
            {item?.livestockOwnership?.notes && (
              <p className="mb-0">
                <strong>Notes:</strong>{' '}
                <span className="fst-italic text-secondary">{item.livestockOwnership.notes}</span>
              </p>
            )}
          </div>
        }
        {Object.hasOwn(item, "beefHousing") && item.beefHousing !== null &&
          <div className="border rounded p-3 mb-3 bg-white shadow-sm h-100">
            <h6 className="fw-bold mb-2">Beef Housing</h6>
            <p className="mb-1">
              <strong>Status:</strong> {housingTypesDictionary[item.beefHousing.status]}
            </p>
          </div>
        }
        {Object.hasOwn(item, "pigsHousing") && item.pigsHousing !== null &&
          <div className="border rounded p-3 mb-3 bg-white shadow-sm h-100">
            <h6 className="fw-bold mb-2">Pigs Housing</h6>
            <p className="mb-1">
              <strong>Status:</strong> {housingTypesDictionary[item.pigsHousing.status]}
            </p>
          </div>
        }
        {Object.hasOwn(item, "poultryHousing") && item.poultryHousing !== null &&
          <div className="border rounded p-3 mb-3 bg-white shadow-sm h-100">
            <h6 className="fw-bold mb-2">Poultry Housing</h6>
            <p className="mb-1">
              <strong>Status:</strong> {housingTypesDictionary[item.poultryHousing.status]}
            </p>
          </div>
        }
        {Object.hasOwn(item, "sheepHousing") && item.sheepHousing !== null &&
          <div className="border rounded p-3 mb-3 bg-white shadow-sm h-100">
            <h6 className="fw-bold mb-2">Sheep Housing</h6>
            <p className="mb-1">
              <strong>Status:</strong> {housingTypesDictionary[item.sheepHousing.status]}
            </p>
          </div>
        }
        {Object.hasOwn(item, "dairyHousing") && item.dairyHousing !== null &&
          <div className="border rounded p-3 mb-3 bg-white shadow-sm h-100">
            <h6 className="fw-bold mb-2">Dairy Housing</h6>
            <p className="mb-1">
              <strong>Status:</strong> {housingTypesDictionary[item.dairyHousing.status]}
            </p>
          </div>
        }
        {Object.hasOwn(item, "otherHousing") && item.otherHousing !== null &&
          <div className="border rounded p-3 mb-3 bg-white shadow-sm h-100">
            <h6 className="fw-bold mb-2">Other Housing</h6>
            <p className="mb-1">
              <strong>Status:</strong> {housingTypesDictionary[item.otherHousing.status]}
            </p>
          </div>
        }
      </>
    )


  }

  const CollapsibleSection = ({ label, section = {} }) => {
    const [isOpen, setIsOpen] = useState(true);
    const safeEntries = Object.entries(section ?? {});

    // Ensure label is a string before formatting
    const displayLabel = typeof label === 'string'
      ? (questionLabelMap?.[label] ?? formatLabel(label))
      : String(label);

    return (
      <div className="card border-0 shadow-sm mb-4">
        <div
          className="card-header bg-primary bg-opacity-10 fw-bold d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer', transition: 'background 0.3s' }}
          onClick={() => setIsOpen(!isOpen)}
        >
          {displayLabel}
          <span className="ms-2">{isOpen ? '▲' : '▼'}</span>
        </div>
        <div className={`collapse ${isOpen ? 'show' : ''}`}>
          <div className="card-body">
            <div className="row g-3">
              {safeEntries.map(([subKey, subValue]) => {
                const isObject = typeof subValue === 'object' && subValue !== null;
                if (isObject && !subValue?.status) {
                  return (
                    <CollapsibleSection
                      key={subKey}
                      label={subKey}
                      section={subValue}
                    />
                  );
                }
                return (
                  <div className="col-md-6" key={subKey}>
                    {renderItem(subKey, subValue)}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const rootEntries = Object.entries(generalPractices ?? {});

  return (
    <div className="vstack gap-3">
      {rootEntries.map(([key, value]) => {
        if (key?.toLowerCase?.().includes('benchmarking')) return null;

        // Special case: otherLivestock has 'name' property, not 'status'
        if (key === 'livestockOptions' && value !== null) {
          return renderLivestockOptions(key, value);
        }

        const isObject = typeof value === 'object' && value !== null;
        if (isObject && !value?.status) {
          return <CollapsibleSection key={key} label={key} section={value} />;
        }
        return renderItem(key, value);
      })}
    </div>
  );
};

export default GeneralPracticesSummary;
